import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { verifyEmail } from "../services/authService";
import { Footer } from "../components/layout/Footer";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [message, setMessage] = useState("");

  const userId = searchParams.get("user_id") || searchParams.get("userId");
  const code = searchParams.get("code") || searchParams.get("token");

  useEffect(() => {
    async function verify() {
      if (!userId || !code) {
        setStatus("error");
        setMessage(
          "Invalid verification link. Please request a new verification email.",
        );
        return;
      }

      try {
        const response = await verifyEmail(userId, code);
        setStatus("success");
        setMessage(response.message || "Email verified successfully!");

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login", {
            state: { message: "Email verified! You can now log in." },
          });
        }, 3000);
      } catch (err) {
        setStatus("error");
        setMessage(err instanceof Error ? err.message : "Verification failed");
      }
    }

    verify();
  }, [userId, code, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
    <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-4">
            {[
              {
                icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Account Verified",
                desc: "Your account is being activated",
              },
              {
                icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                title: "Secure Access",
                desc: "Protected by industry-standard encryption",
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Start Trading",
                desc: "Build and backtest your strategies",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={feature.icon}
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-surface-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - Verification Status */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* Status card */}
          <div className="glass-card p-8 animate-slide-up">
            {status === "loading" && (
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-6">
                  <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Verifying Email
                </h2>
                <p className="text-surface-400">
                  Please wait while we verify your email address...
                </p>
              </div>
            )}

            {status === "success" && (
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Email Verified!
                </h2>
                <p className="text-surface-400 mb-6">{message}</p>
                <p className="text-surface-500 text-sm mb-4">
                  Redirecting to login in 3 seconds...
                </p>
                <Link
                  to="/login"
                  className="btn-primary w-full inline-block text-center"
                >
                  Go to Login Now
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Verification Failed
                </h2>
                <p className="text-red-400 mb-6">{message}</p>
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="btn-primary w-full inline-block text-center"
                  >
                    Go to Login
                  </Link>
                  <Link
                    to="/signup"
                    className="btn-secondary w-full inline-block text-center"
                  >
                    Create New Account
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
